import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Grid, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type {
  BlueprintSection,
  DefinitionDataSection,
  ReportDefinition,
  ReportDefinitionData,
} from '../../../types';
import { useSectionEditor } from '../../hooks/useSectionEditor';
import { AvailableColumnsList } from '../available-columns/AvailableColumnsList';
import { SelectedColumnsList } from '../selected-columns/SelectedColumnsList';

export interface SectionColumnsLayoutProps {
  blueprintSection: BlueprintSection;
  definitionSection: DefinitionDataSection;
}

/**
 * Renders the two-column layout for section columns: selected (report) columns
 * and available columns, with drag-and-drop reordering for selected columns.
 * Owns section column state via useSectionEditor and form watch for columns.
 */
export function SectionColumnsLayout({
  blueprintSection,
  definitionSection,
}: SectionColumnsLayoutProps) {
  const {
    handleAddColumn,
    handleRemoveColumn,
    handleReorderColumn,
    handleUpdateColumnLabel,
    handleUpdateColumnType,
  } = useSectionEditor(blueprintSection.id);

  // Use watch to get the actual form values (which have the correct structure)
  // This ensures we get the real column data with proper IDs, not the tracking IDs from useFieldArray
  const { watch } = useFormContext<ReportDefinition>();
  const formData = watch('data');
  const formSections = (formData?.sections || []) as ReportDefinitionData['sections'];
  const formSectionIndex = formSections.findIndex((s) => s.id === blueprintSection.id);
  const formColumns =
    formSectionIndex !== -1
      ? formSections[formSectionIndex]?.columns || []
      : definitionSection.columns || [];
  const columns = formColumns.length > 0 ? formColumns : definitionSection.columns || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const activeIndex = columns.findIndex((col) => col.id === activeId);
    const overIndex = columns.findIndex((col) => col.id === overId);

    if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
      handleReorderColumn(activeIndex, overIndex);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            <FormattedMessage
              id="audit.reports.section.reportColumns"
              defaultMessage="Columnas del reporte"
            />
          </Typography>
          <SelectedColumnsList
            blueprintSection={blueprintSection}
            definitionSection={definitionSection}
            columns={columns}
            onRemoveColumn={handleRemoveColumn}
            onReorderColumn={handleReorderColumn}
            onUpdateColumnLabel={handleUpdateColumnLabel}
            onUpdateColumnType={handleUpdateColumnType}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>
            <FormattedMessage
              id="audit.reports.section.availableColumns"
              defaultMessage="Columnas disponibles"
            />
          </Typography>
          <AvailableColumnsList
            blueprintSection={blueprintSection}
            definitionSection={definitionSection}
            onAddColumn={handleAddColumn}
          />
        </Grid>
      </Grid>
    </DndContext>
  );
}
